import { useTranslation } from 'react-i18next'
import { CircleCheck, CircleDashed, CirclePause, CircleX, Eye, type LucideIcon } from 'lucide-react'
import { StatusPill, type StatusPillSize, type Tone } from './DeliveryStatusBadge'
import { StatusSelect } from './StatusSelect'

/** The user-set state of a creative, as the API names it. */
export type CreativeState = 'NOT_READY' | 'REQUIRES_REVIEW' | 'APPROVED' | 'REJECTED' | 'PAUSED'

/**
 * The states a user can pick. REQUIRES_REVIEW and REJECTED come from the
 * review process; picking Approved on a rejected creative resubmits it.
 * NOT_READY is left out as nobody uses it, but a creative already in it still
 * shows its badge.
 */
export const SELECTABLE_CREATIVE_STATES: CreativeState[] = ['APPROVED', 'PAUSED']

const TONES: Record<CreativeState, Tone> = {
  NOT_READY: 'gray',
  REQUIRES_REVIEW: 'blue',
  APPROVED: 'green',
  PAUSED: 'amber',
  REJECTED: 'red',
}

const ICONS: Record<CreativeState, LucideIcon> = {
  NOT_READY: CircleDashed,
  REQUIRES_REVIEW: Eye,
  APPROVED: CircleCheck,
  PAUSED: CirclePause,
  REJECTED: CircleX,
}

/** A creative's state as a coloured pill. */
export function CreativeStateBadge({
  state,
  size,
  trailing,
}: {
  state: string
  size?: StatusPillSize
  trailing?: React.ReactNode
}) {
  const { t } = useTranslation()
  if (!state) return <span className="text-xs text-(--ad-text-muted)">—</span>
  return (
    <StatusPill
      tone={TONES[state as CreativeState] ?? 'gray'}
      icon={ICONS[state as CreativeState]}
      size={size}
      trailing={trailing}
    >
      {t(`creativeStates.${state}`, { defaultValue: state })}
    </StatusPill>
  )
}

export interface CreativeStateSelectProps {
  value: string
  /** Left out, the badge is shown on its own. */
  onChange?: (state: CreativeState) => void
  size?: StatusPillSize
}

/** Badge that doubles as a picker for a creative's state. */
export function CreativeStateSelect({ value, onChange, size }: CreativeStateSelectProps) {
  const { t } = useTranslation()
  return (
    <StatusSelect
      value={value}
      onChange={onChange}
      states={SELECTABLE_CREATIVE_STATES}
      size={size}
      describe={(state) => t(`creativeStates.descriptions.${state}`)}
      renderBadge={(state, trailing) => (
        <CreativeStateBadge state={state} size={size} trailing={trailing} />
      )}
    />
  )
}
